/**
 * =============================================================================
 * Página: AffiliateAcceptInvite
 * Rota:   /afiliado/aceitar-convite/:token
 * Versão: 1.1.0
 *
 * Descrição:
 *   Página para usuários autenticados que clicam em um convite. A RPC cria o
 *   registro de afiliado quando necessário, cria a adesão ao programa e valida
 *   que o e-mail da sessão corresponde ao destinatário do convite.
 * =============================================================================
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2, AlertCircle, CheckCircle2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { Button } from '@/components/ui/button';
import { affiliateLoginPath } from '@/lib/affiliate-urls';

export default function AffiliateAcceptInvite() {
  const { token = '', tenantSlug } = useParams<{ token: string; tenantSlug?: string }>();
  const navigate = useNavigate();
  const { session, ready, refresh } = useAffiliateAuth();
  const [state, setState] = useState<'loading' | 'ok' | 'error'>('loading');
  const [message, setMessage] = useState<string>('');
  // Evita duas chamadas concorrentes causadas por reexecuções do efeito.
  const acceptanceStartedRef = useRef(false);

  useEffect(() => {
    if (!ready) return;
    if (!session?.user) {
      // Mantém o path canônico por tenant no next quando disponível
      const acceptPath = tenantSlug
        ? `/afiliado/${tenantSlug}/aceitar-convite/${token}`
        : `/afiliado/aceitar-convite/${token}`;
      navigate(
        `${affiliateLoginPath(tenantSlug)}?next=${encodeURIComponent(acceptPath)}`,
        { replace: true },
      );
      return;
    }

    if (!token) {
      setState('error');
      setMessage('Link de convite inválido.');
      return;
    }

    if (acceptanceStartedRef.current) return;
    acceptanceStartedRef.current = true;

    (async () => {
      const { data, error } = await supabase.rpc(
        'accept_affiliate_program_invite_for_existing',
        { p_token: token } as any,
      );

      if (error) {
        setState('error');
        setMessage(error.message ?? 'Não foi possível aceitar o convite.');
        return;
      }

      const row = (data as any[])?.[0];
      // Recarrega o diretório antes de entrar no painel para que o guard já
      // encontre o novo afiliado e sua adesão ativa.
      await refresh();

      setState('ok');
      setMessage('Você agora participa deste programa!');
      // Redireciona para o painel do afiliado após exibir a confirmação.
      window.setTimeout(() => {
        navigate('/afiliado', { replace: true });
      }, 1500);
      void row;
    })();
  }, [ready, session?.user?.id, token, tenantSlug, navigate, refresh]);

  return (
    <div className="min-h-[100dvh] flex items-center justify-center p-6 bg-background">
      <div className="max-w-md w-full text-center space-y-4 border rounded-2xl p-8">
        {state === 'loading' && (
          <>
            <Loader2 className="h-8 w-8 animate-spin mx-auto text-primary" />
            <p className="text-sm text-muted-foreground">Aceitando convite…</p>
          </>
        )}
        {state === 'ok' && (
          <>
            <div className="mx-auto h-14 w-14 rounded-2xl bg-primary/10 text-primary flex items-center justify-center">
              <CheckCircle2 className="h-7 w-7" />
            </div>
            <h1 className="text-xl font-semibold">Convite aceito</h1>
            <p className="text-sm text-muted-foreground">{message}</p>
          </>
        )}
        {state === 'error' && (
          <>
            <div className="mx-auto h-14 w-14 rounded-2xl bg-destructive/10 text-destructive flex items-center justify-center">
              <AlertCircle className="h-7 w-7" />
            </div>
            <h1 className="text-xl font-semibold">Não foi possível aceitar</h1>
            <p className="text-sm text-muted-foreground">{message}</p>
            <Button onClick={() => navigate('/afiliado', { replace: true })}>
              Ir para o painel
            </Button>
          </>
        )}
      </div>
    </div>
  );
}
